import { useState, useCallback } from "react";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import { api, formatarErroApi } from "@/api";
import { toast } from "sonner";
import { CartaoJogo } from "./CartaoJogo";

function ItemJogoDraggable({ jogo, ...props }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: jogo.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative">
      <div className="absolute left-0 top-0 bottom-0 flex items-center pl-2 cursor-grab active:cursor-grabbing" {...attributes} {...listeners}>
        <GripVertical className="w-5 h-5 text-white/40 hover:text-white/60" />
      </div>
      <div className="pl-10">
        <CartaoJogo jogo={jogo} {...props} />
      </div>
    </div>
  );
}

export function GerenciadorJogosDragDrop({ jogos, onDeletar, onEditar, onPalpitar, modo = "admin" }) {
  const [jogosList, setJogosList] = useState(jogos);
  const [salvando, setSalvando] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { distance: 8 }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = jogosList.findIndex((j) => j.id === active.id);
      const newIndex = jogosList.findIndex((j) => j.id === over.id);

      const novaLista = arrayMove(jogosList, oldIndex, newIndex);
      setJogosList(novaLista);

      // Salvar no backend
      await salvarOrdenacao(novaLista);
    }
  };

  const salvarOrdenacao = useCallback(async (lista) => {
    setSalvando(true);
    try {
      const payload = {
        jogos: lista.map((jogo, index) => ({
          id: jogo.id,
          ordem: index + 1,
        })),
      };

      await api.post("/jogos/reordenar", payload);
      toast.success("Ordem dos jogos atualizada");
    } catch (e) {
      toast.error(formatarErroApi(e));
      // Reverter para lista anterior em caso de erro
      setJogosList(jogos);
    } finally {
      setSalvando(false);
    }
  }, [jogos]);

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={jogosList.map((j) => j.id)} strategy={verticalListSortingStrategy}>
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {jogosList.map((jogo) => (
            <ItemJogoDraggable
              key={jogo.id}
              jogo={jogo}
              modo={modo}
              onDeletar={onDeletar}
              onEditar={onEditar}
              onPalpitar={onPalpitar}
            />
          ))}
        </div>
      </SortableContext>
      {salvando && (
        <div className="fixed bottom-4 right-4 bg-verdebrasil text-white px-4 py-2 rounded-lg">
          Salvando ordem...
        </div>
      )}
    </DndContext>
  );
}
